import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    SearchInput,
    useTranslate,
    useEditContext,
    ListProps,
    FunctionField,
    useResourceContext,
    WrapperField,
    Pagination,
    DateField
} from 'react-admin';
import CustomEmpty from './CustomEmpty';
import RoleNameField from '../user/RoleNameField';
import SubjectListField from '../user/SubjectListField';
import PeopleIcon from '@mui/icons-material/People';
import CustomLogAsTeacherButton from './CustomLogAsTeacherButton';
import CustomLogAsStudentButton from './CustomLogAsStudentButton';
import SessionBadge from '../user/controls/SessionBadge';
import ConditionalLoginField from '../user/ConditionalLoginField';
import ChangeUserStatus from '../user/ChangeUserStatus';
import SourceNameField from '../user/SourceNameField';
import { Stack } from '@mui/material';

interface Props extends ListProps {
    bulkActionButtons?:
        | false
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | undefined;
    sortable?: boolean;
    showExtra?: any;
    hideFields?: any;
    customFilters?: any;
    refreshKey?: string;
    storeKey?: string;
    emptyIcon?: any;
}

//const customRowClick = (id: any, basePath: any, record: any) => {console.log("customRowClick", id, basePath, record); return false;};
//return "https://oppimateriaalit-aspa.otava.fi/#/pupil";

const CustomUserList = ({
    bulkActionButtons,
    sortable = true,
    showExtra = [],
    hideFields = [],
    customFilters = [],
    refreshKey = 'refreshAll',
    storeKey = 'customUserList',
    emptyIcon,
    ...props
}: Props) => {
    const translate = useTranslate();
    const parent = useEditContext();
    const resource = useResourceContext();
    var filter = {};
    var icon = PeopleIcon;

    if (props.filter) filter = props.filter;

    if (emptyIcon) icon = emptyIcon;
    //const filter = props.customFilters?.includes("subscriber_code") ? {"subscriber_guid": parent.record.parent_group[0].guid} :  undefined;
    if (customFilters?.includes('group')) filter['group'] = parent.record.guid;

    if (customFilters?.includes('parent_group'))
        filter['parent_group'] = parent.record.guid;
    //skip_parent
    if (customFilters?.includes('skip_parent')) filter['skip_parent'] = true;

    return (
        <List
            perPage={50} 
            pagination={<Pagination rowsPerPageOptions={[10,25,50,100,200,{label:'Kaikki (max 500)', value:500}]} />}
            {...props}
            actions={false}
            filter={filter}
            filters={
                showExtra?.includes('search')
                    ? [<SearchInput source="q" size="small" alwaysOn />]
                    : undefined
            }
            empty={<CustomEmpty icon={icon} />}
        >
            <Datagrid
                bulkActionButtons={bulkActionButtons}
                rowClick="edit" /* rowClick={(id: any, basePath: any, record: any) => {window.open("https://oppimateriaalit-aspa.otava.fi/#/"+basePath+"/"+id, "_blank");return false;}} */
            >
                {!hideFields?.includes('lastname') && (
                    <WrapperField
                        label="resources.userlist.fields.lastname"
                        sortBy="public.lastname"
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <ChangeUserStatus
                                button={false}
                                action={'remove'}
                                school_guid={parent.record.guid}
                            />
                            <TextField
                                source="public.lastname"
                                sortable={sortable}
                            />
                        </Stack>
                    </WrapperField>
                )}
                {!hideFields?.includes('middlename') && (
                    <TextField
                        source="public.middlename"
                        label={translate(
                            'resources.userlist.fields.middlename'
                        )}
                        sortable={sortable}
                    />
                )}
                {!hideFields?.includes('firstname') && (
                    <TextField
                        source="public.firstname"
                        label={translate('resources.userlist.fields.firstname')}
                        sortable={sortable}
                    />
                )}
                {!hideFields?.includes('code') && (
                    <TextField
                        source="public.code"
                        label={translate('resources.userlist.fields.code')}
                        sortable={sortable}
                    />
                )}
                {!hideFields?.includes('username') && (
                    <TextField
                        source="username"
                        label={translate('resources.userlist.fields.username')}
                        sortable={sortable}
                    />
                )}
                {!hideFields?.includes('email') && (
                    <TextField
                        source="public.email"
                        label={translate('resources.userlist.fields.email')}
                        sortable={sortable}
                    />
                )}
                {!hideFields?.includes('role') && (
                    <RoleNameField
                        label={translate('resources.userlist.fields.role')}
                        sortable={sortable}
                        school_guid={parent.record.guid}
                    />
                )}
                {!hideFields?.includes('subjects') && (
                    <SubjectListField
                        label={translate('resources.userlist.fields.subjects')}
                        sortable={sortable}
                        school_guid={parent.record.guid}
                    />
                )}
                {!hideFields?.includes('registered') && (
                    <DateField
                        source="registered"
                        key="registered"
                        showTime={false}
                        locales="fi-FI"
                    />
                )}
                <SessionBadge
                    source="last_login"
                    options={{
                        showTTL: false,
                    }}
                    sortByOrder="DESC"
                >
                    <ConditionalLoginField
                        showTime
                        locales="fi-FI"
                        sortable={sortable}
                        allConditions={true}
                        button={true}
                    />
                </SessionBadge>

                <FunctionField
                    source="joined"
                    key="joined"
                    sortable={false}
                    sortByOrder="DESC"
                    render={record => {
                        let group =
                            resource === 'group'
                                ? record.classes.find(
                                      s => s.guid === parent.record.id
                                  )
                                : record.schools.find(
                                      s => s.guid === parent.record.id
                                  );
                        if (!group) return '';
                        return group.status === 0 ? (
                            new Date(group.joined).toLocaleDateString('fi-FI')
                        ) : (
                            <span
                                title={`${new Date(
                                    group.joined
                                ).toLocaleDateString('fi-FI')}`}
                            >
                                {translate(
                                    `resources.user.status.${group.status}`
                                )}
                            </span>
                        );
                    }}
                />

                {!hideFields?.includes('loginteacher') && (
                    <CustomLogAsTeacherButton />
                )}
                {!hideFields?.includes('loginstudent') && (
                    <CustomLogAsStudentButton />
                )}
                {!hideFields?.includes('source') && <SourceNameField label="Lähde" size="small" />}
            </Datagrid>
        </List>
    );
};

export default CustomUserList;
